/** STORY-113 part 1, student side — the sign-up offer, as a footnote and never a wall.
 *
 * One line, under the join screen and at the end of a lesson, and only where the course
 * policy allows it. Declining costs nothing: there is no dismiss button because there is
 * nothing to dismiss — the lesson is already running underneath. With `request` the line says
 * plainly that the teacher decides, so nobody expects an account to appear; while a request
 * waits, the line says so instead of asking again; once a teacher has answered, active or
 * declined, the offer stops.
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { RegistrationStatus, Signups } from "../live/studentIdentity";

export function SignupFootnote({
  policy,
  sessionId,
  status = "none",
}: {
  policy: Signups;
  sessionId?: string;
  status?: RegistrationStatus;
}) {
  const { t } = useTranslation();
  if (policy === "off") return null; // the teacher said no: nothing is shown, anywhere
  if (status === "active" || status === "declined") return null;
  if (status === "pending") {
    return (
      <p className="mx-auto w-full max-w-md text-sm leading-relaxed text-[color:var(--ink-2)]" role="status">
        {t("signup.footnote.waiting")}
      </p>
    );
  }
  return (
    <p className="mx-auto w-full max-w-md text-sm leading-relaxed text-[color:var(--ink-2)]">
      {policy === "request" ? t("signup.footnote.request") : t("signup.footnote.open")}{" "}
      <Link
        to="/signup"
        search={{ session: sessionId }}
        /* The hit area is 48 px tall; the negative margin cancels its contribution to the
           line box, so the sentence keeps ordinary leading and stays one line of prose. */
        className="inline-block px-1 py-[0.875rem] -my-[0.875rem] underline"
      >
        {t("signup.footnote.cta")}
      </Link>
    </p>
  );
}
